import { Injectable, inject } from '@angular/core'

import { Observable } from 'rxjs'

import {
  CreateResourceDataSlicesPayload,
  ResourceDataSlice,
  UpdateResourceDataSlicesPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import {
  CreateReusableFunctionPayload,
  ReusableFunction,
  ReusableFunctionWithSlices,
  TestReusableFunctionPayload,
  UpdateReusableFunctionPayload,
  UpdateReusableFunctionScriptPayload,
  saveParamsResponse,
} from '@flowx/designer/modules/reusable-resources/reusable-functions/models/reusable-function.entity'

import { ApiHttpProxyService, ForwardProxyMicroservice } from './api-http-proxy.service'
import { DEFAULT_PAGINATION_PARAMS, PaginationQueryParams } from './api-http.service'

import {
  AppResource,
  AppResourceList,
  Application,
  ApplicationDependencyType,
  ApplicationResourceType,
  ManifestInfo,
  ProxyApiAction,
} from '../entities/application.entity'
import { PaginatedResponse } from '../services/paginated-datasource'

@Injectable({ providedIn: 'root' })
export class ReusableFunctionApiService extends ApiHttpProxyService {
  private url = 'api/business-rule'
  private processApiPath = inject<string>('PROCESS_API_PATH' as any)

  getReusableFunctions(
    params: PaginationQueryParams
  ): Observable<AppResource<PaginatedResponse<ReusableFunction>>> {
    return this.get(
      `${this.url}/findPaginated`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableFunction,
      },
      {
        params: this.addParams({ ...DEFAULT_PAGINATION_PARAMS, ...params }),
      }
    )
  }

  getAllReusableFunctions(): Observable<
    AppResourceList<ReusableFunctionWithSlices[]> & { manifestInfo: Array<ManifestInfo> }
  > {
    return this.get(
      `${this.url}/findAll`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableFunction,
        includeDependentResources: true,
      },
      {
        params: this.addParams({
          includeManifestInfo: true,
          depTypes: ApplicationDependencyType.LIB,
        }),
      }
    )
  }

  getReusableFunction(resourceId: string): Observable<AppResource<ReusableFunction>> {
    return this.get(`${this.url}/getById`, {
      microservice: ForwardProxyMicroservice.Integration,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceId,
    })
  }

  createReusableFunction(
    reusableFunction: CreateReusableFunctionPayload
  ): Observable<AppResource<ReusableFunction>> {
    return this.post(
      this.url,
      {
        action: ProxyApiAction.CREATE,
        resourceName: reusableFunction.name,
        payload: reusableFunction,
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableFunction,
      }
    )
  }

  updateReusableFunction(
    reusableFunction: UpdateReusableFunctionPayload
  ): Observable<AppResource<ReusableFunction>> {
    return this.patch(`${this.url}`, reusableFunction, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceId: reusableFunction.resourceId,
      action: ProxyApiAction.UPDATE,
      resourceName: reusableFunction.name,
    })
  }

  updateReusableFunctionScript(
    resourceId: string,
    resourceName: string,
    reusableFunction: UpdateReusableFunctionScriptPayload
  ): Observable<AppResource<ReusableFunction>> {
    return this.patch(`${this.url}/script`, reusableFunction, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceId,
      action: ProxyApiAction.UPDATE,
      resourceName,
    })
  }
  saveParamsPath(resourceId: string, params: any): Observable<AppResource<saveParamsResponse>> {
    return this.patch(`${this.url}/data-slice/from-paths`, params, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
      action: ProxyApiAction.UPDATE,
    })
  }

  deleteReusableFunction(resourceId: string): Observable<void> {
    return this.delete(
      `${this.url}`,
      { params: { force: true } },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableFunction,
        resourceId,
      }
    )
  }

  testFunction(
    id: number,
    appId: Application['id'],
    appVersionId: Application['versionId'],
    payload: TestReusableFunctionPayload
  ): Observable<Record<string, any>> {
    const processApiPath = this.processApiPath ? this.processApiPath.replace(/^\/?/, '') + '/' : ''
    const url = `${processApiPath}api/test/business-rules`
    return this.httpClient.post(`${this.getBaseUrl()}/${url}`, {
      params: payload.params,
      appId,
      appVersionId,
      businessRule: {
        id,
        expression: payload.script,
        language: payload.language,
        useGraalVM: payload.useGraalVM,
        businessRuleReferences: payload.businessRuleReferences,
        inputParamsType: payload.inputParamsType,
        outputParamsType: payload.outputParamsType,
        inputSlice: payload.inputSlice,
        outputSlice: payload.outputSlice,
      },
    })
  }

  getRbrDataSlices(resourceId: string): Observable<{ resourceContent: ResourceDataSlice[] }> {
    return this.get(`${this.url}/data-slice`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceId,
    })
  }

  createRbrDataSlice(
    resourceId: string,
    payload: CreateResourceDataSlicesPayload
  ): Observable<{ resourceContent: ResourceDataSlice }> {
    return this.patch(`${this.url}/data-slice`, payload, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }

  updateRbrDataSlice(
    resourceId: string,
    payload: UpdateResourceDataSlicesPayload
  ): Observable<{ resourceContent: ResourceDataSlice }> {
    return this.patch(`${this.url}/data-slice`, payload, {
      action: ProxyApiAction.UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableFunction,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }
}
